/**
 * StatusPill — coloured badge with icon for visitor/notice statuses.
 *
 * Uses icon + colour (not just colour) for colour-blind safe semantics.
 * Map: pending=clock, approved=check, denied=x, checked_in=log-in,
 * checked_out=log-out, expired=ban.
 */
import * as React from 'react';
import {
  Check,
  Clock,
  LogIn,
  LogOut,
  Ban,
  X
} from 'lucide-react';
import type { VisitorStatus } from '@hooddesk/shared-types';
import { Badge } from '@/components/ui/badge';

const MAP: Record<
  VisitorStatus,
  { label: string; variant: 'info' | 'warn' | 'ok' | 'alert' | 'muted'; Icon: React.ComponentType<{ className?: string }> }
> = {
  pending: { label: 'Pending', variant: 'warn', Icon: Clock },
  approved: { label: 'Approved', variant: 'ok', Icon: Check },
  checked_in: { label: 'Inside', variant: 'info', Icon: LogIn },
  checked_out: { label: 'Exited', variant: 'muted', Icon: LogOut },
  denied: { label: 'Denied', variant: 'alert', Icon: X },
  expired: { label: 'Expired', variant: 'muted', Icon: Ban }
};

interface StatusPillProps {
  status: VisitorStatus;
  className?: string;
}

export function StatusPill({ status, className }: StatusPillProps): JSX.Element {
  const { label, variant, Icon } = MAP[status];
  return (
    <Badge variant={variant} className={className}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span>{label}</span>
    </Badge>
  );
}

/** Trend chip used inside KPI tiles. positive = green up, negative = red down. */
interface TrendChipProps {
  delta: number; // -100..+100, sign-aware
  unit?: string;
  className?: string;
}

export function TrendChip({ delta, unit = '%', className }: TrendChipProps): JSX.Element {
  const positive = delta >= 0;
  return (
    <Badge variant={positive ? 'ok' : 'alert'} className={className}>
      <span aria-hidden="true">{positive ? '▲' : '▼'}</span>
      <span>{Math.abs(delta).toFixed(0)}{unit}</span>
    </Badge>
  );
}
